import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Alert,
  Dialog,
  Switch,
  FormControlLabel,
  Stack,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as RunIcon,
  Schedule as ScheduleIcon,
  Event as EventIcon,
  AccessTime as TimeIcon,
  CompareArrows as CompareIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import apiService from '../services/api';
import { ScheduledAnalysis, ScheduleType, DateRangeType } from '../types/scheduled';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ScheduleDialog from '../components/analysis/ScheduleDialog';

const ScheduledAnalysesPage: React.FC = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<ScheduledAnalysis | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const queryClient = useQueryClient();

  // Fetch scheduled analyses
  const { data: schedules, isLoading, error } = useQuery({
    queryKey: ['scheduledAnalyses'],
    queryFn: () => apiService.getScheduledAnalyses(),
    refetchInterval: 30000  // Refresh every 30 seconds
  });

  // Mutations
  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      apiService.toggleScheduledAnalysis(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduledAnalyses'] });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteScheduledAnalysis(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduledAnalyses'] });
      setDeleteConfirmId(null);
    }
  });

  const runNowMutation = useMutation({
    mutationFn: (id: number) => apiService.runScheduledAnalysisNow(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['scheduledAnalyses'] });
      alert(`Analysis started! Job ID: ${data.job_id}`);
    }
  });

  const handleCreateNew = () => {
    setEditingSchedule(null);
    setDialogOpen(true);
  };

  const handleEdit = (schedule: ScheduledAnalysis) => {
    setEditingSchedule(schedule);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSchedule(null);
  };

  const handleDialogSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['scheduledAnalyses'] });
    handleCloseDialog();
  };

  const formatSchedule = (schedule: ScheduledAnalysis): string => {
    const time = schedule.schedule_time.substring(0, 5);  // HH:MM
    
    switch (schedule.schedule_type) {
      case ScheduleType.ONCE:
        return `Once at ${time}`;
      case ScheduleType.DAILY:
        return `Daily at ${time}`;
      case ScheduleType.WEEKLY:
        return `Weekly ${schedule.schedule_days} at ${time}`;
      case ScheduleType.MONTHLY:
        return `Monthly at ${time}`;
      default:
        return `${schedule.schedule_type} at ${time}`;
    }
  };

  const formatDateRange = (dateRangeType: DateRangeType): string => {
    return dateRangeType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const formatNextRun = (nextRun?: string): string => {
    if (!nextRun) return 'Not scheduled';
    const date = new Date(nextRun);
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    
    if (diff < 0) return 'Overdue';
    if (diff < 3600000) return `in ${Math.round(diff / 60000)} min`;
    if (diff < 86400000) return `in ${Math.round(diff / 3600000)} hr`;
    return `in ${Math.round(diff / 86400000)} days`;
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <Alert severity="error">Failed to load scheduled analyses</Alert>;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScheduleIcon fontSize="large" color="primary" />
              Scheduled Analyses
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Automate recurring analysis jobs with customizable schedules
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
            size="large"
          >
            Create Schedule
          </Button>
        </Stack>
      </Box>

      {/* Schedules List */}
      {schedules && schedules.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No scheduled analyses yet. Create your first automated analysis schedule!
        </Alert>
      )}

      <Grid container spacing={3}>
        {schedules?.map((schedule) => (
          <Grid item xs={12} md={6} lg={4} key={schedule.id}>
            <Card
              sx={{
                height: '100%',
                border: schedule.is_active ? '2px solid' : '1px solid',
                borderColor: schedule.is_active ? 'primary.main' : 'divider',
                backgroundColor: schedule.is_active ? 'background.paper' : 'action.hover'
              }}
            >
              <CardContent>
                {/* Header */}
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {schedule.name}
                    </Typography>
                    {schedule.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {schedule.description}
                      </Typography>
                    )}
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={schedule.is_active}
                        onChange={(e) => toggleMutation.mutate({ id: schedule.id, isActive: e.target.checked })}
                        size="small"
                      />
                    }
                    label={<Typography variant="caption">{schedule.is_active ? 'Active' : 'Inactive'}</Typography>}
                    labelPlacement="start"
                    sx={{ m: 0 }}
                  />
                </Stack>

                {/* Configuration Info */}
                <Stack spacing={1.5} sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Schedule
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TimeIcon fontSize="small" color="primary" />
                      <Typography variant="body2">{formatSchedule(schedule)}</Typography>
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Date Range
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <EventIcon fontSize="small" color="primary" />
                      <Typography variant="body2">{formatDateRange(schedule.date_range_type)}</Typography>
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Devices
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {schedule.device_ids && schedule.device_ids.length > 0 ? (
                        schedule.device_ids.map((deviceId: string) => (
                          <Chip key={deviceId} label={deviceId.substring(0, 12) + '...'} size="small" />
                        ))
                      ) : (
                        <Chip label="No devices selected" size="small" color="warning" />
                      )}
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Analysis Type
                    </Typography>
                    <Chip 
                      label={schedule.analysis_config.analysis_type.replace('_', ' ').toUpperCase()}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                </Stack>

                {/* Status */}
                <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, mb: 2 }}>
                  <Stack spacing={0.5}>
                    {schedule.is_active && schedule.next_run_at && (
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        Next Run: {formatNextRun(schedule.next_run_at)}
                      </Typography>
                    )}
                    {schedule.is_active && schedule.next_run_at && (
                      <Typography variant="caption" color="text.secondary">
                        {new Date(schedule.next_run_at).toLocaleString('en-US', { 
                          month: 'numeric', 
                          day: 'numeric', 
                          year: 'numeric', 
                          hour: 'numeric', 
                          minute: '2-digit',
                          hour12: true 
                        })} (Local Time)
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      Runs: {schedule.run_count} | Created: {new Date(schedule.created_at).toLocaleDateString()}
                    </Typography>
                    {schedule.last_run_at && (
                      <Typography variant="caption" color="text.secondary">
                        Last Run: {new Date(schedule.last_run_at).toLocaleString()}
                      </Typography>
                    )}
                    {schedule.last_error && (
                      <Typography variant="caption" color="error.main">
                        Error: {schedule.last_error}
                      </Typography>
                    )}
                  </Stack>
                </Box>

                {/* Run Comparison */}
                {schedule.last_run_result?.comparison && schedule.run_count >= 2 && (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      mb: 2,
                      bgcolor: schedule.last_run_result.comparison.has_changes
                        ? 'rgba(255, 152, 0, 0.08)'
                        : 'rgba(76, 175, 80, 0.08)',
                      border: '1px solid',
                      borderColor: schedule.last_run_result.comparison.has_changes
                        ? 'warning.main'
                        : 'success.main',
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <CompareIcon
                        fontSize="small"
                        color={schedule.last_run_result.comparison.has_changes ? 'warning' : 'success'}
                      />
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600 }}
                        color={
                          schedule.last_run_result.comparison.has_changes
                            ? 'warning.main'
                            : 'success.main'
                        }
                      >
                        {schedule.last_run_result.comparison.has_changes
                          ? 'Changes detected vs. previous run'
                          : 'No changes vs. previous run'}
                      </Typography>
                    </Stack>
                    <Stack spacing={0.25} sx={{ pl: 3.5 }}>
                      {Object.entries(schedule.last_run_result.comparison.changes)
                        .map(([key, info]: [string, any]) => (
                          <Typography key={key} variant="caption" color="text.secondary">
                            {info.label}:{' '}
                            {info.previous} &rarr; {info.current}
                            {info.direction !== 'unchanged' && (
                              <span
                                style={{
                                  color: info.delta > 0 ? '#2e7d32' : '#c62828',
                                  fontWeight: 600,
                                }}
                              >
                                {' '}({info.delta > 0 ? '+' : ''}
                                {info.delta})
                              </span>
                            )}
                          </Typography>
                        ))}
                    </Stack>
                  </Box>
                )}

                {/* Actions */}
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Tooltip title="Run Now">
                    <IconButton
                      size="small"
                      onClick={() => runNowMutation.mutate(schedule.id)}
                      disabled={runNowMutation.isPending}
                    >
                      <RunIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(schedule)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => setDeleteConfirmId(schedule.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Create/Edit Dialog */}
      <ScheduleDialog
        open={dialogOpen}
        schedule={editingSchedule}
        onClose={handleCloseDialog}
        onSuccess={handleDialogSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Delete Scheduled Analysis?
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            This action cannot be undone. The schedule will be permanently deleted.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={() => setDeleteConfirmId(null)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => deleteConfirmId && deleteMutation.mutate(deleteConfirmId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? <CircularProgress size={20} /> : 'Delete'}
            </Button>
          </Stack>
        </CardContent>
      </Dialog>
    </Box>
  );
};

export default ScheduledAnalysesPage;

